<template>
    <!-- Back to Projects Button -->
    <div class="mb-6 p-6" @click="onCancel()">
        <span class="flex items-center text-secondary cursor-pointer hover:text-primary transition-colors">
            <i class="fa-solid fa-arrow-left mr-2"></i>
            <span>Back to Projects</span>
        </span>
    </div>
  <Page>
    <div class="-mt-6">
      <!-- Reuse modal UI as page content -->
      <div class="bg-background-card rounded-lg shadow-card max-w-4xl w-full mx-auto p-6">
        <!-- Project Creation Options and Form (copied from modal) -->
        <div class="mb-8">
          <h2 class="text-text-light font-medium mb-4">Choose how to start:</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              class="border-2 rounded-xl p-4 cursor-pointer transition-all"
              :class="creationMode === 'blank' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'"
              @click="creationMode = 'blank'"
            >
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-text-inverse">
                  <i class="fa-solid fa-plus"></i>
                </div>
                <span class="ml-3 font-medium text-text">Create from Blank</span>
              </div>
              <p class="text-text-light text-sm ml-13">Start with an empty project and customize it to your needs.</p>
            </div>

            <div 
              class="border-2 rounded-xl p-4 cursor-pointer transition-all"
              :class="creationMode === 'template' ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'"
              @click="creationMode = 'template'"
            >
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-text-inverse">
                  <i class="fa-solid fa-file-alt"></i>
                </div>
                <span class="ml-3 font-medium text-text">Choose Template</span>
              </div>
              <p class="text-text-light text-sm ml-13">Start with a pre-configured template for faster setup.</p>
            </div>
          </div>
        </div>

        <div v-if="creationMode === 'template'" class="mb-8">
          <h2 class="text-text-light font-medium mb-4">Choose a Template</h2>
          <div class="md:w-2/3">
            <Select
              v-model="selectedTemplate"
              :options="projectTemplates"
              optionLabel="name"
              optionValue="id"
              filter
              placeholder="Select a project template"
              class="w-full rounded-full border border-border bg-background text-text"
              :disabled="templatesLoading && projectTemplates.length === 0"
              :loading="templatesLoading"
              :pt="{
                  root: 'rounded-full',
                  input: 'rounded-full',
                  panel: 'rounded-full',
                  pcfilter: { root: 'rounded-full' }
              }"
            >
              <template #value="{ placeholder }">
                <span v-if="selectedTemplateObj">
                  <span class="flex items-center gap-3">
                    <span class="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10">
                      <i :class="selectedTemplateObj.icon || 'fa-solid fa-folder' + ' text-xl text-primary'"></i>
                    </span>
                    <span class="flex flex-col">
                      <span class="font-medium text-text">{{ selectedTemplateObj.name }}</span>
                      <span class="text-xs text-text-light">{{ selectedTemplateObj.description }}</span>
                    </span>
                  </span>
                </span>
                <span v-else class="text-text-light">{{ placeholder }}</span>
              </template>
              <template #option="{ option, selected }">
                <div class="flex items-center gap-3 p-2 rounded transition-all" :class="selected ? 'bg-primary/10' : ''">
                  <span class="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10">
                    <i :class="option.icon || 'fa-solid fa-folder' + ' text-xl text-primary'"></i>
                  </span>
                  <span class="flex flex-col">
                    <span class="font-medium text-text">{{ option.name }}</span>
                    <span class="text-xs text-text-light">{{ option.description }}</span>
                  </span>
                </div>
              </template>
              <template #empty>
                <div class="py-3 text-center text-text-light">
                  <span v-if="templatesLoading">
                    <i class="fa-solid fa-spinner fa-spin mr-2"></i>
                    Loading templates...
                  </span>
                  <span v-else>No templates available</span>
                </div>
              </template>
              <template #loadingicon="{ class: loadingIconClass }">
                <i :class="['fa-solid fa-spinner fa-spin', loadingIconClass]"></i>
              </template>
            </Select>
          </div>
        </div>

        <!-- Project Details Form -->
        <div class="mb-8">
          <h2 class="text-text-light font-medium mb-4">Project Details</h2>
          <div class="space-y-6">
            <div>
              <label for="project-name" class="block text-text font-medium mb-2">Project Name *</label>
              <input 
                type="text" 
                id="project-name" 
                v-model="formData.name"
                class="w-full bg-background border border-border rounded-full px-4 py-3 focus:ring-2 focus:ring-primary focus:outline-hidden text-sm text-text"
                placeholder="Enter project name"
                :class="{ 'border-error': errors.name }"
              />
              <p v-if="errors.name" class="text-error text-sm mt-1">{{ errors.name }}</p>
            </div>

            <div>
              <label for="project-description" class="block text-text font-medium mb-2">Description</label>
              <textarea 
                id="project-description" 
                v-model="formData.description"
                rows="3"
                class="w-full bg-background border border-border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:outline-hidden text-sm text-text resize-none"
                placeholder="Enter project description (optional)"
              ></textarea>
            </div>

            <div>
              <label class="block text-text font-medium mb-2">Project Icon</label>
              <div class="flex flex-wrap gap-3">
                <div 
                  v-for="icon in availableIcons" 
                  :key="icon"
                  class="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all"
                  :class="formData.icon === icon ? 'bg-primary text-text-inverse' : 'bg-background border border-border text-text-light hover:bg-primary/10 hover:text-primary'"
                  @click="formData.icon = icon"
                >
                  <i :class="icon"></i>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-text font-medium mb-2">Team Members</label>
              <div class="space-y-3">
                <div 
                  v-for="(member, index) in members" 
                  :key="member.id + '-' + index"
                  class="flex items-center p-3 bg-background rounded-lg border border-border"
                >
                  <div class="w-10 h-10 rounded-full bg-accent text-text-inverse flex items-center justify-center">
                    <span class="text-sm font-medium">{{ getUserInitials(member) }}</span>
                  </div>
                  <div class="ml-3 flex-1">
                    <p class="font-medium text-text">{{ member.name }}</p>
                    <p class="text-text-light text-sm">{{ member.email }}</p>
                  </div>
                  <div class="flex items-center space-x-2">
                    <div class="relative">
                      <select 
                        v-model="member.role"
                        class="appearance-none bg-accent text-text-inverse px-4 py-2 rounded-full pr-8 focus:outline-hidden text-sm transition-colors hover:bg-accent-dark"
                      >
                        <option v-for="roleKey in Object.keys(ProjectRole)" :key="roleKey" :value="roleKey">
                          {{ pascalToHuman(roleKey) }}
                        </option>
                      </select>
                      <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-text-inverse">
                        <i class="fa-solid fa-chevron-down text-xs"></i>
                      </div>
                    </div>
                    <button 
                      @click="removeTeamMember(index)"
                      class="w-8 h-8 bg-error/10 text-error rounded-full flex items-center justify-center transition-colors hover:bg-error/20"
                      title="Remove member"
                    >
                      <i class="fa-solid fa-times text-xs"></i>
                    </button>
                  </div>
                </div>

                <button 
                  @click="showAddMemberModal = true"
                  class="flex items-center text-primary p-3 w-full justify-center border border-dashed border-border rounded-lg hover:bg-background-hover transition-colors"
                >
                  <i class="fa-solid fa-plus mr-2"></i>
                  <span>Add Team Member</span>
                </button>

                <!-- Error message when no Project Owner -->
                <Message v-if="hasNoProjectOwner" severity="error" class="mt-3">
                  At least one team member must have the Project Owner role.
                </Message>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-4">
          <button 
            @click="onCancel"
            class="px-6 py-2 rounded-full border border-border text-text-light hover:bg-background-hover transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="handleCreateProject"
            :disabled="!isFormValid || loading"
            class="px-6 py-2 rounded-full bg-accent text-text-inverse hover:bg-accent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin mr-2"></i>
            {{ loading ? 'Creating...' : 'Create Project' }}
          </button>
        </div>
      </div>
    </div>

  <AddMembersModal v-model="showAddMemberModal" :existing-members="existingMembersForModal" @add="onAddMember" />
  </Page>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { pascalToHuman } from '@/shared/utils/stringUtils';
import { ProjectRole } from '@/core/types/models/RoleTypes';
import Page from '@/shared/components/layouts/Page.vue';
import AddMembersModal from '../components/AddMembersModal.vue';
import Select from 'primevue/select';
import Message from 'primevue/message';
// project creation logic moved into composable
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { addToast } from '@/shared/utils/toastMessages';
import type { ProjectRoleType } from '@/core/types/models/RoleTypes';
// types handled in composables

definePage({
  meta: {
    requiresProject: false,
  },
});

const router = useRouter();
const route = useRoute();
const toast = useToast();

// handled in useCreateProject

const showAddMemberModal = ref(false);

// Composable: move form & member logic into useCreateProject
import { useCreateProject } from '../composables/useCreateProject';
const {
  formData,
  errors,
  creationMode,
  selectedTemplate,
  members,
  availableIcons,
  selectedTemplateObj,
  loading,
  isFormValid,
  getUserInitials,
  addMembers,
  removeMember,
  submitProject,
  existingMembersForModal,
  projectTemplates,
  templatesLoading,
  hasNoProjectOwner
} = useCreateProject();

// Handle template query parameter from "use as template" action
if (route.query.template) {
  creationMode.value = 'template';
  selectedTemplate.value = route.query.template as string;
}

// Accept either the new bulk selection { users: UiUser[], role: ProjectRoleType }
// or the legacy single-member object for compatibility.
function onAddMember(selection: any) {
  const users = Array.isArray(selection?.users) ? selection.users : (selection ? [selection] : []);
  const role: ProjectRoleType = selection?.role ?? (selection && selection.role) ?? ProjectRole.ProjectContributor as ProjectRoleType;
  addMembers(users, role);
}

function removeTeamMember(index: number) {
  removeMember(index);
}

async function handleCreateProject() {
  try {
    await submitProject(() => router.back());
    addToast(toast, { severity: 'success', summary: 'Project Created', detail: 'Project created successfully' });
  } catch (err: any) {
    addToast(toast, { severity: 'error', summary: 'Error', detail: err?.message || 'Failed to create project' });
  }
}

function onCancel() {
  router.back();
}
</script>
